import { useResourceRevision } from "@authe/ui-core/api";
import { catalogResource } from "@authe/fe-commons/catalog";
/**
 * Auth0-style "Getting Started" progress, INFERRED from real tenant data.
 *
 * Rather than track a brittle server-side checklist, each step's completion is
 * derived from a cheap signal the dashboard already has:
 *
 *  - **wallet**    — a connected browser wallet (`@authe/web3` inventory);
 *  - **team**      — at least one team exists (`GET /teams`);
 *  - **contracts** — at least one stored ABI (`GET /abis`);
 *  - **catalog**   — at least one entity (catalog-mgmt, via the shared
 *    {@link useCatalogApi} port — same GraphQL fast path + `entity → entities`
 *    routing as the rest of the app, never the legacy standalone entities-api);
 *  - **pipeline**  — at least one pipeline definition (`GET /pipelines`).
 *
 * Each probe asks for a single row (`limit=1`) and degrades gracefully — a
 * failing/empty probe just leaves its step "to do", never throwing.
 *
 * A step the reader has no scope for is UNAVAILABLE, which is a third state and
 * not a synonym for "to do". Without it a probe the reader cannot run answered
 * `done: false, loading: false` forever, `allComplete` never tripped, and a
 * plain Reader — who cannot create a team, register an ABI or build a pipeline,
 * and never needed to — was pinned to the setup guide instead of the queue,
 * permanently, with a checklist of things they are not allowed to do. An
 * unanswerable step is not a failed step: it is somebody else's step.
 *
 * The dismissal is identity-scoped (`createScopedStorage`), not raw
 * `localStorage`: written app-wide it is shared by every user of a browser, so
 * on a shared workstation the second analyst inherited the first one's choice.
 * `?guide=1` / the explicit `/getting-started` route can still surface it.
 * Probes are skipped while dismissed so the landing page pays no network cost
 * once the guide is gone.
 */
import * as React from "react";
import { useAddressInventory } from "@authe/web3";
import { useQuery } from "@authe/ui-core/api";
import { usePermissions } from "@authe/ui-core/permissions";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { CatalogItemKind } from "@authe/catalog";
import { useCatalogApi } from "@/api/adapters";
import { useStorageIdentity } from "@/app/storageIdentity";
import { RESOURCES, ROUTES } from "@/config";
import { completion, type Completion, type StepState } from "./progressRules";

export type OnboardingStepId = "wallet" | "team" | "contracts" | "catalog" | "pipeline";

/**
 * A step as the guide renders it: its copy and its route, plus the state the
 * completion rule reads.
 *
 * It EXTENDS `StepState` rather than restating those fields — they were
 * declared twice, and predictably drifted: `failed` and `optional` were added
 * to the rule and the two surfaces that render a step could not see them.
 */
export interface OnboardingStep extends StepState {
  id: OnboardingStepId;
  title: string;
  description: string;
  /** Route the step's CTA navigates to. */
  to: string;
  /** CTA label. */
  cta: string;
}

/**
 * The hook's result: the steps, the completion the rule computed, and the
 * dismissal controls.
 *
 * Extends `Completion` rather than restating its fields — they were declared
 * twice and drifted, so `unknown` (a required probe failed) existed in the rule
 * and was invisible to the page that had to branch on it.
 */
export interface OnboardingProgress extends Completion {
  steps: OnboardingStep[];
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
}

const DISMISS_KEY = "dashboard.onboarding.dismissed";

/** True when a list payload carries at least one row (array / `items` / `results`). */
function hasAnyItem(data: unknown): boolean {
  if (Array.isArray(data)) return data.length > 0;
  if (data && typeof data === "object") {
    const obj = data as { items?: unknown; results?: unknown };
    if (Array.isArray(obj.items)) return obj.items.length > 0;
    if (Array.isArray(obj.results)) return obj.results.length > 0;
  }
  return false;
}

export interface UseOnboardingProgressOptions {
  /**
   * Run the probes even when the guide is dismissed.
   *
   * The landing page leaves this off so a dismissed guide costs no requests.
   * But a page that RENDERS the guide must pass it: gated on `!dismissed`
   * alone, `/getting-started` and `?guide=1` — the two doors that exist
   * precisely for a tenant who has dismissed it — showed "0 of 5 complete"
   * with every card unticked, because the probes they needed were switched
   * off by the dismissal they were overriding.
   */
  probe?: boolean;
}

export function useOnboardingProgress(
  options: UseOnboardingProgressOptions = {},
): OnboardingProgress {
  const identity = useStorageIdentity();
  // Memoised on the FIELDS: `useStorageIdentity` returns a fresh literal every
  // render, so keying on the object would mint a new keyspace per paint. Same
  // reasoning as the sidebar's collapse state.
  const storage = React.useMemo(
    () => createScopedStorage(identity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId],
  );

  // `get` reads up the hierarchy (user → tenant → org → app) and stores JSON, so
  // the value is a boolean rather than the old `"1"` sentinel.
  const [dismissed, setDismissed] = React.useState<boolean>(() =>
    storage.get(DISMISS_KEY, false),
  );

  const dismiss = React.useCallback(() => {
    setDismissed(true);
    storage.set(DISMISS_KEY, true);
  }, [storage]);

  const restore = React.useCallback(() => {
    setDismissed(false);
    storage.remove(DISMISS_KEY);
  }, [storage]);

  // Probes run only while the guide is live (not dismissed), so a satisfied /
  // hidden guide costs nothing on the landing page — and only where the reader
  // has the scope, so a restricted reader's console is not a wall of 403s.
  const { can } = usePermissions();
  // `/teams` is authorized by `teams:read` (teams-mgmt-api's swagger), and
  // `RESOURCES.team` is `"users"` — the team-MEMBERS family, a different thing.
  // A custom role holding one and not the other was pinned to the guide for
  // ever, since the probe could never answer.
  const canTeams = can(RESOURCES.teams, "read");
  const canAbis = can(RESOURCES.abis, "list");
  const canCatalog = can(RESOURCES.catalog, "read");
  const canPipelines = can(RESOURCES.pipelines, "read");

  const enabled = options.probe === true || !dismissed;
  const { isConnected, status: walletStatus } = useAddressInventory();

  const teams = useListProbe("/teams", enabled && canTeams);
  const abis = useListProbe("/abis", enabled && canAbis);
  const entities = useCatalogProbe(enabled && canCatalog);
  const pipelines = useListProbe("/pipelines", enabled && canPipelines);

  const steps = React.useMemo<OnboardingStep[]>(
    () => [
      {
        id: "wallet",
        title: "Connect a wallet",
        description:
          "Link a browser wallet to surface your own on-chain activity and sign actions.",
        to: ROUTES.wallet,
        cta: "Connect wallet",
        done: isConnected,
        // wagmi reports `reconnecting` on a reload before it knows; declaring
        // `loading: false` there let the landing page decide, render the guide,
        // then swap to the queue — the content swap the decision hold exists to
        // prevent.
        loading: walletStatus === "connecting" || walletStatus === "reconnecting",
        // Purely client-side (a browser wallet), so it is never gated.
        unavailable: false,
        // Offered, not owed — see `StepState.optional`. A workspace with teams,
        // ABIs, a catalog and pipelines is set up whether or not anyone plugged
        // a wallet into this browser.
        optional: true,
      },
      {
        id: "team",
        title: "Invite your team",
        description:
          "Add teammates so they can collaborate on investigations and back you up on the account.",
        to: ROUTES.team,
        cta: "Invite members",
        done: teams.done,
        loading: teams.loading,
        failed: teams.failed,
        unavailable: !canTeams,
      },
      {
        id: "contracts",
        title: "Upload contract ABIs",
        description:
          "Register ABIs so transactions touching your contracts decode into readable calls and events.",
        to: ROUTES.contracts,
        cta: "Add ABIs",
        done: abis.done,
        loading: abis.loading,
        failed: abis.failed,
        unavailable: !canAbis,
      },
      {
        id: "catalog",
        title: "Build your catalog",
        description:
          "Create tags, labels, entities and groups to attribute addresses and enrich every transaction view.",
        to: ROUTES.catalog,
        cta: "Open the catalog",
        done: entities.done,
        loading: entities.loading,
        failed: entities.failed,
        unavailable: !canCatalog,
      },
      {
        id: "pipeline",
        title: "Create a pipeline",
        description:
          "Process your protocol's transactions your way — decode, enrich and route them through a pipeline.",
        to: ROUTES.pipelines,
        cta: "Build a pipeline",
        done: pipelines.done,
        loading: pipelines.loading,
        failed: pipelines.failed,
        unavailable: !canPipelines,
      },
    ],
    [
      isConnected,
      walletStatus,
      teams,
      abis,
      entities,
      pipelines,
      canTeams,
      canAbis,
      canCatalog,
      canPipelines,
    ],
  );

  // The rule lives in `./progressRules` so it can be tested without these five
  // probes and four providers — it is the part that was wrong.
  return { steps, ...completion(steps), dismissed, dismiss, restore };
}

interface ProbeResult {
  done: boolean;
  loading: boolean;
  /** The read errored, so "not done" would be a guess. */
  failed: boolean;
}

/** One list probe: `GET {path}?limit=1` → `{ done: hasItems, loading }`. */
function useListProbe(path: string, enabled: boolean): ProbeResult {
  const query = useQuery<unknown>(
    (client, signal) => client.get<unknown>(path, { query: { limit: 1 }, signal }),
    {
      resourcePolicy: { independent: "Configuration probes; the catalog probe subscribes separately." }, enabled, deps: [path, enabled] },
  );
  return React.useMemo(
    () => ({
      done: hasAnyItem(query.data),
      loading: enabled && query.isLoading,
      failed: enabled && Boolean(query.error),
    }),
    [query.data, query.isLoading, query.error, enabled],
  );
}

/**
 * Catalog step probe — asks catalog-mgmt for a single entity through the shared
 * {@link useCatalogApi} port (`list("entity", { limit: 1 })`), so it rides the
 * same GraphQL fast path + `entity → entities` routing as the rest of the app
 * instead of the legacy standalone entities-api. `CatalogApi.list` already
 * unwraps to a `CatalogRow[]`, so there's no envelope to feed `useQuery`; a tiny
 * effect drives the `{ done, loading, failed }` result. It never throws — but a
 * failure is REPORTED rather than swallowed as "to do", because a swallowed one
 * turned a set-up tenant's front door into "Getting started · Add your first
 * entity". Skipped when the guide is neither shown nor live, so the landing
 * page pays no network cost for a dismissed guide.
 */
function useCatalogProbe(enabled: boolean): ProbeResult {
  const revision = useResourceRevision(enabled ? [catalogResource("entity")] : []);
  const api = useCatalogApi();
  const [result, setResult] = React.useState<ProbeResult>(() => ({
    done: false,
    loading: enabled,
    failed: false,
  }));

  React.useEffect(() => {
    if (!enabled) {
      setResult({ done: false, loading: false, failed: false });
      return;
    }
    let active = true;
    setResult((prev) => ({ done: prev.done, loading: true, failed: false }));
    api
      .list(CatalogItemKind.ENTITY, { limit: 1 })
      .then((page) => {
        if (active) setResult({ done: page.items.length > 0, loading: false, failed: false });
      })
      .catch(() => {
        if (active) setResult({ done: false, loading: false, failed: true });
      });
    return () => {
      active = false;
    };
  }, [api, enabled, revision]);

  return result;
}
